import React, { useEffect, useMemo, useRef, useState } from 'react';

// MD components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

// MUI
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import { useTheme, alpha } from '@mui/material/styles';

import { useStaffSplitLists } from 'sectors/pilates/staff/hooks/useStaffList';
import { useMemberSearch } from 'sectors/pilates/members/hooks/useMemberSearch';

// ---- helpers
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return startOfDay(x);
}
function endOfWeek(d) {
  const s = startOfWeek(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return endOfDay(e);
}
function startOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  return startOfDay(x);
}
function endOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return endOfDay(x);
}
function minusMonths(d, months) {
  const x = new Date(d);
  x.setMonth(x.getMonth() - months);
  return x;
}
function toISODateInput(d) {
  if (!d) return '';
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const PERIODS = [
  { value: 'TODAY', label: 'Bugün' },
  { value: 'WEEK', label: 'Bu hafta' },
  { value: 'MONTH', label: 'Bu ay' },
  { value: 'LAST_6M', label: 'Son 6 ay' },
  { value: 'CUSTOM', label: 'Özel' },
];

export default function ActivityLogsFiltersBar({ value, onChange, onReset, onList, listing }) {
  const theme = useTheme();

  const set = (patch) => onChange({ ...value, ...patch });
  const { period, from, to, memberId, trainerId, memberQ, channel } = value;

  // 🔵 sayfa/spot ışık (mavi)
  const accent = '#A9D6F5';
  // ✅ toggle selected (hafif yeşil)
  const green = theme.palette.success.main;

  // ---------- SOFT / NO BORDER LOOK ----------
  const cardSx = {
    border: 'none',
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: alpha(theme.palette.background.paper, 0.92),
    backdropFilter: 'blur(10px)',
    backgroundImage:
      `radial-gradient(900px 360px at 50% -10%, ${alpha(accent, 0.18)}, transparent 62%),` +
      `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, transparent 42%)`,
    backgroundRepeat: 'no-repeat',
    boxShadow: `0 14px 34px ${alpha(theme.palette.common.black, 0.1)}`,
  };

  const headerSx = {
    px: 2,
    py: 1.25,
    backgroundColor: alpha(theme.palette.background.paper, 0.55),
    backdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    boxShadow: `0 10px 26px ${alpha(theme.palette.common.black, 0.06)}`,
  };

  const bodySx = { px: 2, py: 1.5 };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.25,
      backgroundColor: alpha(theme.palette.common.white, 0.6),
      backdropFilter: 'blur(8px)',
      transition: 'all 160ms ease',

      '& fieldset': {
        borderColor: alpha(theme.palette.text.primary, 0.12),
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: alpha(theme.palette.text.primary, 0.16),
      },
      '&.Mui-focused fieldset': { borderColor: alpha(accent, 0.35) },
      '&.Mui-focused': { boxShadow: `0 0 0 4px ${alpha(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root': { fontSize: 12.5 },
    '& .MuiFormHelperText-root': { opacity: 0.7 },
  };

  // period -> date range
  useEffect(() => {
    const now = new Date();
    if (period === 'TODAY') set({ from: startOfDay(now), to: endOfDay(now) });
    if (period === 'WEEK') set({ from: startOfWeek(now), to: endOfWeek(now) });
    if (period === 'MONTH') set({ from: startOfMonth(now), to: endOfMonth(now) });
    if (period === 'LAST_6M') set({ from: startOfDay(minusMonths(now, 6)), to: endOfDay(now) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // trainers
  const { workingList, passiveList, isLoading: trainersLoading } = useStaffSplitLists();

  const trainerOptions = useMemo(() => {
    const all = [...(workingList || []), ...(passiveList || [])];
    const map = new Map();
    all.forEach((s) => {
      if (!s?.id) return;
      const fullName =
        s.fullName || s.name || `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || `#${s.id}`;
      map.set(String(s.id), { id: s.id, name: fullName, working: s.working });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [workingList, passiveList]);

  const ALL_TRAINERS = { id: null, name: 'Hepsi' };
  const trainerOptionsWithAll = useMemo(() => [ALL_TRAINERS, ...trainerOptions], [trainerOptions]);

  const selectedTrainer = useMemo(() => {
    if (trainerId == null) return null;
    return trainerOptionsWithAll.find((t) => String(t.id) === String(trainerId)) || null;
  }, [trainerId, trainerOptionsWithAll]);

  // members
  const {
    loading: memLoading,
    results: memResults,
    total: memTotal,
    search: searchMembers,
  } = useMemberSearch();

  const [memOpen, setMemOpen] = useState(false);
  const [memPage, setMemPage] = useState(0);
  const [memOptions, setMemOptions] = useState([]);

  const [memberInput, setMemberInput] = useState(memberQ || '');
  useEffect(() => setMemberInput(memberQ || ''), [memberQ]);

  const PAGE_SIZE = 10;
  const ALL_MEMBERS = { id: null, name: 'Hepsi', surname: '', fullName: 'Hepsi' };

  const memberLabel = (m) => {
    if (!m) return '';
    if (m.id == null) return 'Hepsi';
    const name = (m?.name || '').trim();
    const surname = (m?.surname || '').trim();
    const full = `${name} ${surname}`.trim();
    return full || m?.fullName || `#${m?.id}`;
  };

  const hasMoreMembers =
    typeof memTotal === 'number'
      ? memOptions.length < memTotal
      : (memResults || []).length === PAGE_SIZE;

  const loadMembers = (pageToLoad) => {
    const qq = (value.memberQ || '').trim();
    searchMembers({ q: qq, page: pageToLoad, size: PAGE_SIZE });
  };

  useEffect(() => {
    if (!memOpen) return;
    setMemOptions((prev) => {
      const base = memPage === 0 ? [] : [...prev];
      const map = new Map(base.map((x) => [String(x.id), x]));
      (memResults || []).forEach((m) => m?.id && map.set(String(m.id), m));
      return Array.from(map.values());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memResults]);

  const memberOptionsWithAll = useMemo(() => [ALL_MEMBERS, ...memOptions], [memOptions]);

  const selectedMember = useMemo(() => {
    if (memberId == null) return null;
    return memberOptionsWithAll.find((m) => String(m.id) === String(memberId)) || null;
  }, [memberId, memberOptionsWithAll]);

  const debounceRef = useRef(null);

  const handleMemberOpen = () => {
    setMemOpen(true);
    setMemPage(0);
    setMemOptions([]);
    loadMembers(0);
  };

  const handleMemberInputChange = (e, text, reason) => {
    // ✅ seçince input'u doldurabilsin
    if (reason === 'reset') {
      setMemberInput(text);
      return;
    }

    // (clear vb. durumlar)
    if (reason === 'clear') {
      setMemberInput('');
      set({ memberQ: '' });
      setMemPage(0);
      setMemOptions([]);
      return;
    }

    setMemberInput(text);
    set({ memberQ: text });

    setMemPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadMembers(0), 250);
  };

  const handleMemberListScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 36;
    if (!nearBottom || memLoading || !hasMoreMembers) return;

    const next = memPage + 1;
    setMemPage(next);
    loadMembers(next);
  };

  const handleReset = () => {
    set({ channel: 'UI' });
    setMemberInput('');
    setMemOptions([]);
    setMemPage(0);
    onReset?.();
  };

  return (
    <MDBox sx={cardSx}>
      {/* header */}
      <MDBox sx={headerSx}>
        <MDBox>
          <MDTypography variant="button" fontWeight="medium">
            Filtreler
          </MDTypography>
        </MDBox>

        <MDBox display="flex" alignItems="center" gap={1}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={channel || 'UI'}
            onChange={(e, v) => {
              if (!v) return;
              set({ channel: v });
            }}
            disabled={listing}
            sx={{
              mr: 0.75,
              p: 0.35,
              borderRadius: 999,
              border: 'none',
              backgroundColor: alpha(theme.palette.background.paper, 0.55),
              backdropFilter: 'blur(10px)',
              boxShadow:
                `0 1px 0 ${alpha(theme.palette.common.white, 0.45)} inset, ` +
                `0 10px 22px ${alpha(theme.palette.common.black, 0.08)}`,

              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                borderRadius: 999,
                mx: 0.2,
              },

              '& .MuiToggleButton-root': {
                textTransform: 'none',
                borderRadius: 999,
                px: 1.4,
                py: 0.65,
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                color: alpha(theme.palette.text.primary, 0.78),
                backgroundColor: 'transparent',
                transition: 'all 180ms ease',
                minHeight: 32,

                '&:hover': { backgroundColor: alpha(accent, 0.12) },

                // ✅ normal state: iç border/glow yok (daha temiz)
                boxShadow: 'none',
              },

              // ✅ SELECTED: hafif yeşil
              '& .MuiToggleButton-root.Mui-selected': {
                color: theme.palette.success.dark,
                background: `linear-gradient(180deg, ${alpha(green, 0.16)}, ${alpha(green, 0.08)})`,
                boxShadow:
                  `0 0 0 1px ${alpha(green, 0.26)} inset, ` + `0 10px 22px ${alpha(green, 0.14)}`,
                '&:hover': {
                  background: `linear-gradient(180deg, ${alpha(green, 0.2)}, ${alpha(green, 0.1)})`,
                },
              },

              '&.Mui-disabled': {
                opacity: 0.6,
                boxShadow: 'none',
                backdropFilter: 'none',
              },
            }}
          >
            <ToggleButton value="UI">UI</ToggleButton>
            <ToggleButton value="ALL">Tümü</ToggleButton>
          </ToggleButtonGroup>

          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            onClick={handleReset}
            disabled={listing}
            sx={{
              minWidth: 120,
              borderRadius: '999px',
              border: 'none',
              backgroundColor: alpha(theme.palette.background.paper, 0.55),
              boxShadow: `0 10px 22px ${alpha(theme.palette.common.black, 0.08)}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.75),
              },
            }}
          >
            <Icon fontSize="small">restart_alt</Icon>&nbsp;Sıfırla
          </MDButton>

          <MDButton
            variant="gradient"
            color="success"
            size="small"
            onClick={onList}
            disabled={listing}
            sx={{
              minWidth: 120,
              borderRadius: '999px',
              boxShadow: `0 12px 26px ${alpha(theme.palette.success.main, 0.18)}`,
            }}
          >
            {listing ? <CircularProgress size={16} color="inherit" /> : <Icon>search</Icon>}
            &nbsp;Listele
          </MDButton>
        </MDBox>
      </MDBox>

      {/* body */}
      <MDBox sx={bodySx}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.25}
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          {/* period */}
          <Autocomplete
            size="small"
            options={PERIODS}
            value={PERIODS.find((p) => p.value === period) || PERIODS[0]}
            onChange={(e, v) => set({ period: v?.value || 'TODAY' })}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(params) => <TextField {...params} label="Dönem" sx={inputSx} />}
            sx={{ width: { xs: '100%', lg: 170 } }}
          />

          {/* date range */}
          <TextField
            size="small"
            type="date"
            label="Başlangıç"
            value={toISODateInput(from)}
            disabled={period !== 'CUSTOM'}
            onChange={(e) =>
              set({ from: e.target.value ? startOfDay(new Date(e.target.value)) : null })
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', lg: 160 }, ...inputSx }}
          />
          <TextField
            size="small"
            type="date"
            label="Bitiş"
            value={toISODateInput(to)}
            disabled={period !== 'CUSTOM'}
            onChange={(e) =>
              set({ to: e.target.value ? endOfDay(new Date(e.target.value)) : null })
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', lg: 160 }, ...inputSx }}
          />

          {/* member */}
          <Autocomplete
            size="small"
            open={memOpen}
            onOpen={handleMemberOpen}
            onClose={() => setMemOpen(false)}
            options={memberOptionsWithAll}
            value={selectedMember}
            onChange={(e, v) => {
              set({ memberId: v?.id ?? null });

              // ✅ seçilen üye input'ta gözüksün
              setMemberInput(memberLabel(v));

              // opsiyonel: arama query'sini temizle (istersen)
              // set({ memberQ: '' });
            }}
            inputValue={memberInput}
            onInputChange={handleMemberInputChange}
            getOptionLabel={(o) => memberLabel(o)}
            isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
            loading={memLoading}
            ListboxProps={{
              onScroll: handleMemberListScroll,
              style: { maxHeight: 260, overflow: 'auto' },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Üye"
                placeholder="Seç / Yaz (Hepsi)"
                sx={inputSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {memLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                helperText={
                  memOpen
                    ? typeof memTotal === 'number'
                      ? `${Math.min(memberOptionsWithAll.length - 1, memTotal)}/${memTotal}`
                      : 'Aşağı kaydır → daha fazla'
                    : ' '
                }
                FormHelperTextProps={{ sx: { m: 0, mt: 0.25, opacity: 0.65 } }}
              />
            )}
            sx={{ minWidth: { lg: 260 }, flex: 1 }}
          />

          {/* trainer */}
          <Autocomplete
            size="small"
            options={trainerOptionsWithAll}
            value={selectedTrainer}
            onChange={(e, v) => set({ trainerId: v?.id ?? null })}
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
            loading={trainersLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Eğitmen"
                placeholder="Seç (Hepsi)"
                sx={inputSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {trainersLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: { xs: '100%', lg: 260 } }}
          />
        </Stack>
      </MDBox>
    </MDBox>
  );
}
